import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { CategoryDeleteBlockedData } from '@avisoma/shared';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../catalyst/dialog';
import { Button } from '../../catalyst/button';
import { Text } from '../../catalyst/text';
import backend from '../../services/backend';
import { useToast } from '../../components/Toast';
import { readCategoryGuardError } from '../../lib/categoryGuardError';

/**
 * The server refuses a delete it considers unsafe (409) rather than cascading the
 * damage, so the dialog's job is to explain WHICH guard fired and what the user can do
 * about it — a bare "request failed" would leave a reassignable category looking as
 * permanent as a code-referenced one.
 */
function blockedMessage(data: CategoryDeleteBlockedData): string {
  if (data.reason === 'pages_linked') {
    // Deliberately does not promise that reassigning unblocks the delete — the server
    // checks several other guards after this one, and a protected name never passes.
    return `${data.linkedPageCount} page${data.linkedPageCount === 1 ? ' is' : 's are'} still labelled "${data.name}", so the server refuses to delete it: the delete would strip the labels the classifier trains on.`;
  }
  if (data.reason === 'documents_linked') {
    const count = data.linkedDocumentCount ?? 0;
    return `${count} document${count === 1 ? ' is' : 's are'} still classified as "${data.name}", so the server refuses to delete it. A document keeps the category name even before its pages are split out.`;
  }
  if (data.reason === 'supertype_referenced') {
    const names = (data.supertypes ?? []).map((supertype) => `"${supertype}"`).join(', ');
    return `The supertype spec${(data.supertypes ?? []).length === 1 ? '' : 's'} ${names} still reference${(data.supertypes ?? []).length === 1 ? 's' : ''} "${data.name}". Remove it from the spec first — a leftover reference silently shifts every document's supertype score.`;
  }
  return `"${data.name}" is referenced by name in extraction code, so deleting it would silently stop extraction for pages classified under it. It cannot be deleted.`;
}

/** The shared reader, plus this dialog's own longer-form copy for a recognised guard. */
function readError(error: unknown): { message: string; blocked: CategoryDeleteBlockedData | null } {
  const failure = readCategoryGuardError(error);
  if (failure.blocked !== null) {
    return { message: blockedMessage(failure.blocked), blocked: failure.blocked };
  }
  return { message: failure.message, blocked: null };
}

export function DeleteCategoryDialog({
  open,
  onClose,
  categoryId,
  name,
  linkedPageCount,
}: {
  open: boolean;
  onClose: () => void;
  categoryId: string;
  name: string;
  linkedPageCount: number;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const del = useMutation({
    mutationFn: () => backend.deleteCategory(categoryId).then((res) => res.data),
    onSuccess: () => {
      toast.success(`Category "${name}" deleted`);
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.removeQueries({ queryKey: ['getCategory', categoryId] });
      onClose();
      navigate('/categories');
    },
  });

  const failure = del.error ? readError(del.error) : null;

  // The mutation keeps its error until reset, so a dialog reopened after a 409 would
  // otherwise show the stale banner — and stay permanently un-deletable once a
  // protected_name failure had disabled the button.
  const close = () => {
    del.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={del.isPending ? () => {} : close}>
      <DialogTitle>Delete {name}?</DialogTitle>
      <DialogBody className="space-y-3">
        <Text>
          This permanently deletes the category, its embedding clusters and its release-certificate
          embeddings. The classification results derived from it are removed too; pages themselves are
          kept.
        </Text>
        {/* States only what is certain. Reassigning the pages does not by itself make the
            delete succeed — several other guards run after that one, and a code-referenced
            category never becomes deletable. */}
        {linkedPageCount > 0 && (
          <Text>
            {linkedPageCount} page{linkedPageCount === 1 ? ' is' : 's are'} still labelled with it, and the
            server refuses to delete a category while pages carry its label.
          </Text>
        )}
        {failure && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            {failure.message}
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={close} disabled={del.isPending}>
          {failure?.blocked ? 'Close' : 'Cancel'}
        </Button>
        {/* Only `protected_name` is unresolvable from the UI, so only it disables the
            button. `pages_linked` and `supertype_referenced` are both fixable elsewhere
            in the app — leave Delete live so a retry works once they are. */}
        <Button
          color="red"
          onClick={() => del.mutate()}
          disabled={del.isPending || failure?.blocked?.reason === 'protected_name'}
        >
          {del.isPending ? 'Deleting…' : 'Delete Category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
